import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import {
  deserializeFicheDePreps,
  serializeFicheDePreps,
} from '@teetsh/app/src/queries/sequences/serializer';
import { FicheDePrep } from '@teetsh/app/src/types/sequences';
import { SEQUENCES_LIST_KEY } from '@teetsh/app/src/queries/sequences/useSequencesFolders';
import { standaloneFdpQueryKeys } from '@teetsh/app/src/queries/fdp/useStandaloneFicheDePrep';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useLimitReached } from '@teetsh/app/src/components/LimitReached/useLimitReached';
import { useTranslation } from 'react-i18next';

function createStandaloneFicheDePrep(
  fdp: FicheDePrep,
  folderId?: string,
  templateId?: string,
) {
  const url = templateId
    ? `/fiches-de-prep?strapi-template-id=${templateId}`
    : '/fiches-de-prep';

  return request(url, {
    method: 'POST',
    body: {
      ...serializeFicheDePreps(fdp),
      folderId,
    },
  }).then(v => deserializeFicheDePreps(v));
}

export function useCreateStandaloneFicheDePrep() {
  const queryClient = useQueryClient();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['sequence']);
  const { openLimitModal } = useLimitReached();

  return useMutation({
    mutationFn: ({
      ficheDePrep,
      folderId,
      templateId,
    }: {
      ficheDePrep?: FicheDePrep;
      folderId?: string;
      templateId?: string;
    }) => {
      // If templateId is provided, create from template (empty FicheDePrep)
      const fdp = ficheDePrep || ({} as FicheDePrep);
      return createStandaloneFicheDePrep(fdp, folderId, templateId);
    },
    onSuccess: fdp => {
      queryClient.setQueryData(standaloneFdpQueryKeys.detail(fdp.id), fdp);
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });
    },
    onError: err => {
      if (err?.response?.status === 403) {
        openLimitModal({
          requiredPlan: (err?.response as any)?.data?.requiredPlan,
          limit: (err?.response as any)?.data?.limit,
        });
        return;
      }
      notifyError({
        title: t('sequence:messages.seance.createError'),
      });
    },
  });
}
